import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { pushDropdownCloser } from '../../utils/dropdownEscapeStack';

export interface CpmFilterOption {
    value: string;
    label: string;
}

interface CpmFilterDropdownProps {
    label: string;
    options: readonly CpmFilterOption[] | CpmFilterOption[];
    selected: string[];
    onChange: (next: string[]) => void;
    multi?: boolean;
    emptyLabel?: string;
    /**
     * When true (default), render emptyLabel as the first menu row.
     * Active when nothing is selected; clicking clears the filter.
     * Opt out for sort / form selects that already include their own default.
     */
    includeEmptyOption?: boolean;
    className?: string;
    /** Leading icon inside the trigger (e.g. sort / calendar). */
    leadingIcon?: ReactNode;
    /** Override the trigger text (e.g. short sort label). */
    triggerLabel?: string;
    /** Chip triggers only: fill the pill when a value is selected. Select triggers never ring just because they have a value. */
    highlightSelected?: boolean;
    minWidth?: number;
    /**
     * Open the menu upward instead of downward (design's `menuUpStyle`).
     * Use for dropdowns living in a footer/bottom-of-card row so the menu
     * doesn't get clipped by an `overflow-hidden` ancestor.
     */
    openUp?: boolean;
    /** Disable the trigger (form selects inside a busy card). */
    disabled?: boolean;
    /** Warning chrome — e.g. a rank whose pack is no longer in the seen list. */
    error?: boolean;
    /**
     * Render the menu on document.body so overflow-hidden ancestors cannot clip it.
     * Use for selects inside rounded/clipped cards.
     */
    portal?: boolean;
    /** Trigger height in px. Default 38 (filters). Form selects in the prototype are 42. */
    triggerHeight?: number;
    /**
     * `select` (default) = value-in-trigger dropdown used across CPM forms.
     * `chip` = 30px pill trigger showing only `label`, filled when active —
     * matches the design system's `Chip` component for list-page filters.
     */
    triggerVariant?: 'select' | 'chip';
    /** Floor width for a portaled menu. Chip triggers are short; copy-from State is 274. */
    menuMinWidth?: number;
    loading?: boolean;
    loadingLabel?: string;
    failed?: boolean;
    failedTitle?: string;
    failedBody?: string;
    onRetry?: () => void;
}

function MenuCheck() {
    return (
        <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--cpm-brand-purple)"
            strokeWidth="3"
        >
            <path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

export default function CpmFilterDropdown({
    label,
    options,
    selected,
    onChange,
    multi = true,
    emptyLabel = 'Any',
    includeEmptyOption = true,
    className = '',
    leadingIcon,
    triggerLabel,
    highlightSelected = true,
    minWidth,
    openUp = false,
    disabled = false,
    error = false,
    portal = false,
    triggerHeight = 38,
    triggerVariant = 'select',
    menuMinWidth = 210,
    loading = false,
    loadingLabel = 'Loading…',
    failed = false,
    failedTitle = 'Couldn’t load options',
    failedBody = 'The options did not come back. Nothing on the list changed.',
    onRetry,
}: CpmFilterDropdownProps) {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const [menuPos, setMenuPos] = useState<{
        top: number;
        left: number;
        width: number;
        placeUp: boolean;
    } | null>(null);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => {
            const target = e.target as Node;
            if (rootRef.current?.contains(target)) return;
            if (portal && menuRef.current?.contains(target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open, portal]);

    useEffect(() => {
        if (disabled && open) setOpen(false);
    }, [disabled, open]);

    useEffect(() => {
        if (!open) return;
        const pop = pushDropdownCloser(() => setOpen(false));
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            e.preventDefault();
            e.stopPropagation();
            setOpen(false);
        };
        document.addEventListener('keydown', onKey);
        return () => {
            pop();
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    useLayoutEffect(() => {
        if (!open || !portal) {
            setMenuPos(null);
            return;
        }
        const update = () => {
            const trigger = triggerRef.current;
            const menu = menuRef.current;
            if (!trigger) return;
            const rect = trigger.getBoundingClientRect();
            const menuHeight = menu?.offsetHeight ?? 180;
            const gap = 6;
            const spaceBelow = window.innerHeight - rect.bottom - gap;
            const placeUp = openUp || (spaceBelow < menuHeight && rect.top > spaceBelow);
            const top = placeUp ? rect.top - gap - menuHeight : rect.bottom + gap;
            const width = Math.max(rect.width, menuMinWidth);
            let left = rect.left;
            left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
            setMenuPos({ top, left, width, placeUp });
        };
        update();
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [open, portal, openUp, options, menuMinWidth, loading, failed]);

    const hasSelection = selected.length > 0;
    const showSelectedChrome = highlightSelected && hasSelection;
    const display =
        triggerLabel ??
        (!hasSelection
            ? emptyLabel
            : selected.map((v) => options.find((o) => o.value === v)?.label ?? v).join(', '));

    const clearAll = () => {
        onChange([]);
        setOpen(false);
    };

    const toggle = (value: string) => {
        if (!multi) {
            onChange(selected.includes(value) ? [] : [value]);
            setOpen(false);
            return;
        }
        if (selected.includes(value)) {
            onChange(selected.filter((v) => v !== value));
        } else {
            onChange([...selected, value]);
        }
    };

    const itemClass = (active: boolean) =>
        [
            'flex items-center gap-2 px-2.5 py-2 rounded-[var(--cpm-radius-8)] text-left transition-colors',
            active
                ? 'text-[var(--cpm-brand-purple)]'
                : 'text-[var(--cpm-text-primary)] hover:bg-[var(--cpm-surface-subtle)]',
        ].join(' ');

    const menuInner = loading ? (
        <div style={{ padding: '2px 4px 4px' }}>
            <div className="flex items-center" style={{ gap: 9, padding: '2px 0 12px' }}>
                <span className="cpm-pulse-dot" />
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--cpm-text-secondary)' }}>
                    {loadingLabel}
                </span>
            </div>
            <div className="flex flex-col" style={{ gap: 8 }}>
                <div className="cpm-sk" style={{ height: 12, width: '74%' }} />
                <div className="cpm-sk" style={{ height: 12, width: '58%' }} />
                <div className="cpm-sk" style={{ height: 12, width: '66%' }} />
            </div>
        </div>
    ) : failed ? (
        <div style={{ padding: '2px 4px 4px' }}>
            <div style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--cpm-shortfall-text)' }}>
                {failedTitle}
            </div>
            <div
                style={{
                    marginTop: 4,
                    fontSize: 11.5,
                    fontWeight: 600,
                    lineHeight: 1.45,
                    color: 'var(--cpm-text-secondary)',
                }}
            >
                {failedBody}
            </div>
            {onRetry ? (
                <div style={{ marginTop: 10 }}>
                    <button
                        type="button"
                        onClick={onRetry}
                        style={{
                            height: 36,
                            padding: '0 12px',
                            borderRadius: 8,
                            border: '1.5px solid var(--cpm-border-strong)',
                            background: '#fff',
                            fontFamily: 'var(--cpm-font-sans)',
                            fontSize: 12.5,
                            fontWeight: 800,
                            color: 'var(--cpm-text-primary)',
                            cursor: 'pointer',
                        }}
                    >
                        Retry
                    </button>
                </div>
            ) : null}
        </div>
    ) : (
        <>
            {includeEmptyOption ? (
                <button
                    type="button"
                    onClick={clearAll}
                    className={itemClass(!hasSelection)}
                    style={{ fontSize: 13, fontWeight: !hasSelection ? 800 : 700 }}
                >
                    <span className="flex-1">{emptyLabel}</span>
                    {!hasSelection ? <MenuCheck /> : null}
                </button>
            ) : null}
            {options.map((opt) => {
                const active = selected.includes(opt.value);
                return (
                    <button
                        key={opt.value}
                        type="button"
                        onClick={() => toggle(opt.value)}
                        className={itemClass(active)}
                        style={{ fontSize: 13, fontWeight: active ? 800 : 700 }}
                    >
                        <span className="flex-1">{opt.label}</span>
                        {active ? <MenuCheck /> : null}
                    </button>
                );
            })}
        </>
    );

    const menuSurfaceClass =
        'bg-white border border-[var(--cpm-border-default)] rounded-[var(--cpm-radius-10)] shadow-[var(--cpm-shadow-dropdown)] p-[5px] flex flex-col cpm-animate-pop-in max-h-[264px] overflow-y-auto min-w-[210px]';

    const triggerChrome = disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer';
    const triggerBorder = error
        ? 'border-[var(--cpm-warning-border)]'
        : open
          ? 'border-[var(--cpm-brand-purple)] shadow-[var(--cpm-ring-focus)]'
          : 'border-[var(--cpm-border-strong)]';

    if (triggerVariant === 'chip') {
        return (
            <div ref={rootRef} className={['relative inline-flex', className].join(' ')}>
                <button
                    ref={triggerRef}
                    type="button"
                    disabled={disabled}
                    aria-disabled={disabled}
                    aria-expanded={open}
                    onClick={() => {
                        if (disabled) return;
                        setOpen((v) => !v);
                    }}
                    className={[
                        'inline-flex items-center transition-colors duration-150',
                        showSelectedChrome
                            ? ''
                            : 'hover:border-[var(--cpm-brand-purple)] hover:bg-[var(--cpm-page-bg)]',
                        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                    ].join(' ')}
                    style={{
                        height: 30,
                        padding: '0 13px',
                        borderRadius: 'var(--cpm-radius-full)',
                        border: `1.5px solid ${
                            showSelectedChrome
                                ? 'var(--cpm-brand-purple)'
                                : 'var(--cpm-border-strong)'
                        }`,
                        background: showSelectedChrome ? 'var(--cpm-purple-selected)' : '#fff',
                        color: showSelectedChrome
                            ? 'var(--cpm-brand-navy)'
                            : 'var(--cpm-text-primary)',
                        fontFamily: 'var(--cpm-font-sans)',
                        fontSize: 12.5,
                        fontWeight: 800,
                    }}
                >
                    {triggerLabel ?? label}
                </button>
                {open && portal && typeof document !== 'undefined'
                    ? createPortal(
                          <div
                              ref={menuRef}
                              className={`cpm-theme ${menuSurfaceClass}`}
                              style={{
                                  position: 'fixed',
                                  top: menuPos?.top ?? -9999,
                                  left: menuPos?.left ?? -9999,
                                  width: menuPos?.width ?? 210,
                                  zIndex: 500,
                                  visibility: menuPos ? 'visible' : 'hidden',
                              }}
                          >
                              {menuInner}
                          </div>,
                          document.body
                      )
                    : null}
                {open && !portal ? (
                    <div
                        className={[
                            'absolute left-0 z-30',
                            menuSurfaceClass,
                            openUp ? 'bottom-full mb-1.5' : 'top-full mt-1.5',
                        ].join(' ')}
                        style={{ width: 240 }}
                    >
                        {menuInner}
                    </div>
                ) : null}
            </div>
        );
    }

    return (
        <div
            ref={rootRef}
            className={[
                'relative flex flex-col flex-[1_1_180px] min-w-[150px] max-w-[240px]',
                className,
            ].join(' ')}
            style={{ gap: 6, minWidth }}
        >
            {label.trim() ? (
                <span
                    className="uppercase text-[var(--cpm-text-secondary)]"
                    style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.06em' }}
                >
                    {label}
                </span>
            ) : null}
            <button
                ref={triggerRef}
                type="button"
                disabled={disabled}
                aria-disabled={disabled}
                aria-expanded={open}
                onClick={() => {
                    if (disabled) return;
                    setOpen((v) => !v);
                }}
                className={[
                    'rounded-[var(--cpm-radius-10)] border-[1.5px] bg-white flex items-center text-left transition-colors duration-150',
                    triggerChrome,
                    triggerBorder,
                ].join(' ')}
                style={{ height: triggerHeight, paddingLeft: 11, paddingRight: 11, gap: 8 }}
            >
                {leadingIcon}
                <span
                    className={[
                        'flex-1 min-w-0 truncate',
                        showSelectedChrome
                            ? 'text-[var(--cpm-text-primary)]'
                            : 'text-[var(--cpm-text-secondary)]',
                    ].join(' ')}
                    style={{ fontSize: 13, fontWeight: showSelectedChrome ? 800 : 600 }}
                >
                    {display}
                </span>
                <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--cpm-text-tertiary)"
                    strokeWidth="2.2"
                    className="shrink-0 ml-auto"
                >
                    <path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
            {open && portal && typeof document !== 'undefined'
                ? createPortal(
                      <div
                          ref={menuRef}
                          className={`cpm-theme ${menuSurfaceClass}`}
                          style={{
                              position: 'fixed',
                              top: menuPos?.top ?? -9999,
                              left: menuPos?.left ?? -9999,
                              width: menuPos?.width ?? 210,
                              zIndex: 500,
                              visibility: menuPos ? 'visible' : 'hidden',
                          }}
                      >
                          {menuInner}
                      </div>,
                      document.body
                  )
                : null}
            {open && !portal ? (
                <div
                    className={[
                        'absolute left-0 right-0 z-30',
                        menuSurfaceClass,
                        openUp ? 'bottom-full mb-1.5' : 'top-full mt-1.5',
                    ].join(' ')}
                >
                    {menuInner}
                </div>
            ) : null}
        </div>
    );
}
